import { Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { QrPosterTemplate } from '../models/qr-poster-template.model';
import {
  QrPosterColorizedIconAssetName,
  getRequiredQrPosterColorizedIconAssetNames,
} from '../models/qr-poster-icons.model';
import {
  PreparedQrPosterAssets,
  QrPosterRenderDescriptor,
  QrPosterRenderDescriptorResponse,
  QrPosterIconAssets,
} from '../models/qr-poster-render-descriptor.model';
import { CompanyService } from './company.service';
import { PropertyService } from './property.service';

const DEFAULT_COMPANY_LOGO = 'assets/scanafy_company_logo_croped.png';

export interface QrPosterRenderAssetLoader {
  loadLogo(descriptor: QrPosterRenderDescriptor): Promise<Blob | null>;
  loadTemplateImage(descriptor: QrPosterRenderDescriptor, assetKey: string): Promise<Blob | null>;
  loadIcons?(
    descriptor: QrPosterRenderDescriptor,
    requiredIconNames: readonly QrPosterColorizedIconAssetName[],
  ): Promise<QrPosterIconAssets>;
}

export interface QrPosterRenderAssetPreparationOptions {
  prestartedLogoBlob?: Promise<Blob | null>;
}

@Injectable({ providedIn: 'root' })
export class QrPosterRenderAssetsService {
  constructor(
    private readonly companyService: CompanyService,
    private readonly propertyService: PropertyService,
  ) {}

  async prepare(
    response: QrPosterRenderDescriptorResponse,
    assetLoader?: QrPosterRenderAssetLoader,
    options: QrPosterRenderAssetPreparationOptions = {},
  ): Promise<PreparedQrPosterAssets> {
    const template = this.cloneTemplate(response.descriptor.template);
    const requiredIconNames = getRequiredQrPosterColorizedIconAssetNames(template);
    const templateImagesPromise = this.resolveTemplateImages(template, response.descriptor, assetLoader);
    const logoPromise = options.prestartedLogoBlob
      ? this.loadPrestartedLogo(options.prestartedLogoBlob)
      : this.loadLogo(response, assetLoader);
    const iconsPromise: Promise<QrPosterIconAssets> = requiredIconNames.length === 0
      ? Promise.resolve({})
      : assetLoader?.loadIcons
        ? assetLoader.loadIcons(response.descriptor, requiredIconNames)
        : lastValueFrom(this.propertyService.getColoredIcons(
          response.descriptor.branding.themeColor,
          requiredIconNames,
        ));

    const [, logoBase64, icons] = await Promise.all([
      templateImagesPromise,
      logoPromise,
      iconsPromise,
    ]);

    return {
      template,
      logoBase64,
      icons,
      dispose: () => undefined,
    };
  }

  private async loadLogo(
    response: QrPosterRenderDescriptorResponse,
    assetLoader?: QrPosterRenderAssetLoader,
  ): Promise<string> {
    try {
      const blob = assetLoader
        ? await assetLoader.loadLogo(response.descriptor)
        : response.descriptor.property.propertyId === 'editor-preview'
          ? await firstValueFrom(this.companyService.getCompanyLogoBlob())
          : await lastValueFrom(this.propertyService.getQrPosterLogoBlob(response.descriptor.property.propertyId));
      if (!blob) {
        return DEFAULT_COMPANY_LOGO;
      }
      return await this.convertBlobToDataUrl(blob);
    } catch {
      throw new Error('QR poster logo asset could not be loaded');
    }
  }

  private async loadPrestartedLogo(
    prestartedLogoBlob: Promise<Blob | null>,
  ): Promise<string> {
    try {
      const blob = await prestartedLogoBlob;
      if (!blob) {
        return DEFAULT_COMPANY_LOGO;
      }

      return await this.convertBlobToDataUrl(blob);
    } catch {
      throw new Error('QR poster logo asset could not be loaded');
    }
  }

  private async resolveTemplateImages(
    template: QrPosterTemplate,
    descriptor: QrPosterRenderDescriptor,
    assetLoader?: QrPosterRenderAssetLoader,
  ): Promise<void> {
    const imageBlocks = template.blocks.filter((block) => block.type === 'image' && block.content?.imageAssetKey);
    await Promise.all(imageBlocks.map(async (block) => {
      const currentImageUrl = block.content?.imageUrl || '';
      if (currentImageUrl.startsWith('data:image/')) {
        return;
      }

      const assetKey = block.content?.imageAssetKey || '';
      let blob: Blob | null;
      try {
        blob = assetLoader
          ? await assetLoader.loadTemplateImage(descriptor, assetKey)
          : await lastValueFrom(this.companyService.getQrPosterAssetBlob(assetKey));
      } catch {
        throw new Error(`QR poster image asset could not be loaded: ${assetKey}`);
      }

      if (!blob) {
        throw new Error(`QR poster image asset is unavailable: ${assetKey}`);
      }

      block.content = {
        ...(block.content || {}),
        imageUrl: await this.convertBlobToDataUrl(blob),
      };
    }));
  }

  private cloneTemplate(template: QrPosterTemplate): QrPosterTemplate {
    return JSON.parse(JSON.stringify(template)) as QrPosterTemplate;
  }

  private convertBlobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const value = typeof reader.result === 'string' ? reader.result : '';
        if (value) {
          resolve(value);
          return;
        }
        reject(new Error('QR poster asset conversion failed'));
      };
      reader.onerror = () => reject(new Error('QR poster asset conversion failed'));
      reader.readAsDataURL(blob);
    });
  }
}
